// Tracks per-panel manual-analysis activity so the app can reflect overall
// progress in the favicon status dot (useFaviconBadge). Panels are independent and unbounded
// (see ComparisonGrid), so each reports its state here keyed by a stable
// per-instance id; the consumers read the aggregate.
//
// Beyond "is anything running" we accumulate the outcome of the current *batch*
// (a batch = one continuous span where ≥1 panel is running), so the favicon can
// flash green/red when the batch ends. Only manual deep analyses are tracked;
// the live WS subscription is intentionally excluded.
import { create } from 'zustand';

export type PanelActivity = 'running' | 'success' | 'error' | 'idle';

interface TabActivityStore {
  /** Map of panel id → its current manual-analysis state. */
  activity: Record<string, PanelActivity>;
  /** Whether any panel reported an error during the current batch. */
  batchHadError: boolean;
  /** Whether any panel reported a success during the current batch. */
  batchHadSuccess: boolean;
  /** Report a panel's current activity. */
  setActivity: (id: string, next: PanelActivity) => void;
  /** Drop a panel entirely (on unmount). */
  clearActivity: (id: string) => void;
}

const countRunning = (activity: Record<string, PanelActivity>): number =>
  Object.values(activity).filter((a) => a === 'running').length;

export const useTabActivityStore = create<TabActivityStore>()((set) => ({
  activity: {},
  batchHadError: false,
  batchHadSuccess: false,

  setActivity: (id, next) =>
    set((state) => {
      if (state.activity[id] === next) return state; // no change — avoid renders

      const activity = { ...state.activity, [id]: next };
      const runningBefore = countRunning(state.activity);
      const runningAfter = countRunning(activity);

      let { batchHadError, batchHadSuccess } = state;
      // A fresh batch begins when we go from idle to ≥1 running: reset outcomes.
      if (runningBefore === 0 && runningAfter > 0) {
        batchHadError = false;
        batchHadSuccess = false;
      }
      // Outcomes are sticky for the batch's lifetime (a mid-batch error survives
      // even if that panel is later reset to idle).
      if (next === 'error') batchHadError = true;
      if (next === 'success') batchHadSuccess = true;

      return { activity, batchHadError, batchHadSuccess };
    }),

  clearActivity: (id) =>
    set((state) => {
      if (!(id in state.activity)) return state;
      const { [id]: _omit, ...rest } = state.activity;
      return { activity: rest };
    }),
}));

/** Number of panels currently running a manual analysis. */
export const selectBusyCount = (state: TabActivityStore): number => countRunning(state.activity);
